/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { useTraceUpdate } from '../../../hooks/shared/useDebugRenders';
import { useTranslation } from 'react-i18next';
import { Controller, useWatch } from 'react-hook-form';
import { Autocomplete } from '@react-google-maps/api';

import InputComponent from '../../../components/InputMC';
import ButtonComponent from '../../../components/ButtonMC';
import CheckBoxMC from '../../../components/CheckBoxMC';
import DropdownMC from '../../../components/DropdownMC';

/**
 * Componente Organism para gestión de direcciones de cliente (modal moderno).
 *
 * @component ManageCustomerAddressMC
 * @category Component - Organism
 * @description
 * - Modal moderno para crear/editar direcciones de cliente con TailwindCSS.
 * - Basado en la arquitectura de CustomerEventModalMC con separación de capas.
 * - Integra Google Maps Autocomplete y validación de formularios.
 * - Optimizado con hooks, memoización y performance tracking.
 * - Usa i18n para todos los textos y componentes atómicos MC.
 *
 * **Atomic Design:**
 * - Nivel: Organism
 * - Composición: ModalMC, FormProviderMC, InputComponent, ButtonComponent, CheckBoxMC, DropdownMC, Autocomplete
 * - Dependencias: useCustomerAddressForm, useCustomerAddressActions, useCustomerAddressModalUI
 *
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.selectedAddress - Dirección seleccionada para editar (opcional)
 * @param {boolean} props.loadLocation - Cargar ubicación automáticamente (opcional)
 *
 * @returns {JSX.Element} Modal renderizado para gestión de direcciones
 *
 * @example
 * <ManageCustomerAddressMC
 *   customer={customer}
 *   selectedAddress={address}
 * />
 *
 * @since 4.0.0
 * @version 4.0.0 - Nuevo componente basado en CustomerEventModalMC con TailwindCSS
 */
const ManageCustomerAddressMC = ({
  formMethods,
  selectedAddress,
  isLoaded,
  googleMapsApiLoaded,
  onLoadAutocomplete,
  onPlaceChanged,
  openSelectAddressModal,
  errorMain,
  countryOptions,
}) => {
  // Debug: rastreo de renders y cambios de props
  useTraceUpdate('ManageCustomerAddressMC', {
    formMethods,
    selectedAddress,
    isLoaded,
    googleMapsApiLoaded,
    onLoadAutocomplete,
    onPlaceChanged,
    openSelectAddressModal,
    errorMain,
    countryOptions,
  });

  const selectedCountry = useWatch({
    control: formMethods.control,
    name: 'selected_country'
  });

  const { t } = useTranslation('InboxMC');
  return (
    <div className="tw-space-y-4">
      <div>
        {!isLoaded ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-h-12 tw-bg-gray-50 tw-rounded-lg">
            <div className="tw-animate-spin tw-h-5 tw-w-5 tw-border-2 tw-border-blue-500 tw-border-t-transparent tw-rounded-full"></div>
            <span className="tw-ml-2 tw-text-sm tw-text-gray-600">
              {t('location.modal.loading')}
            </span>
          </div>
        ) : (
          <div className="tw-relative">
            {googleMapsApiLoaded && 
             typeof window !== 'undefined' && 
             window.google && 
             window.google.maps && 
             window.google.maps.places && (
              <Autocomplete onLoad={onLoadAutocomplete} onPlaceChanged={onPlaceChanged}>
                <InputComponent
                  required
                  name="address"
                  placeholder={t('location.modal.address_placeholder')}
                  maxLength={500}
                  className="tw-w-full"
                  useFloatingLabel
                  label={t('location.address')}
                />
              </Autocomplete>
            )}
            {(!googleMapsApiLoaded || 
              typeof window === 'undefined' || 
              !window.google || 
              !window.google.maps || 
              !window.google.maps.places) && (
              <InputComponent
                required
                name="address"
                placeholder={t('location.modal.address_placeholder')}
                maxLength={500}
                className="tw-w-full"
                useFloatingLabel
                label={t('location.address')}
              />
            )}
            <label className="tw-absolute tw-text-xs tw-text-blue-500 tw-top-[5px] tw-z-10 tw-start-4">
              {t('location.address')}
            </label>
          </div>
        )}
      </div>

      <div className="tw-flex tw-justify-center">
        <ButtonComponent
          type="button"
          text={
            googleMapsApiLoaded
              ? t('location.modal.search_on_map')
              : t('location.loading_maps')
          }
          onClick={openSelectAddressModal}
          disabled={!googleMapsApiLoaded}
          outlined
          className="!tw-bg-white !tw-text-blue-500 tw-text-default tw-font-medium tw-px-8"
        />
      </div>

      <div>
        <InputComponent
          name="description"
          rows={3}
          placeholder={t('location.modal.description_placeholder')}
          maxLength={500}
          className="tw-w-full tw-resize-none tw-placeholder-gray-400"
          floatingLabelClassName="tw-start-3"
          containerClass="tw-flex-1 tw-min-w-0"
          label={t('location.description')}
          textarea
          useFloatingLabel
        />
      </div>

      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
        <div>
          <InputComponent
            name="city"
            placeholder={t('location.modal.city_placeholder')}
            required
            className="tw-w-full"
            useFloatingLabel
            label={t('location.city')}
          />
        </div>

        <div>
          <InputComponent
            name="state"
            placeholder={t('location.modal.state_placeholder')}
            required
            className="tw-w-full"
            useFloatingLabel
            label={t('location.state')}
          />
        </div>

        <div>
          <InputComponent
            name="zip_code"
            placeholder={t('location.modal.zip_code_placeholder')}
            className="tw-w-full"
            useFloatingLabel
            label={t('location.zip_code')}
          />
        </div>

        <div>
          <DropdownMC
            useFloatingLabel
            name="selected_country"
            label={t('location.country')}
            defaultValue={selectedCountry || selectedAddress?.country_id}
            options={countryOptions.map((country) => ({
              id: country.value,
              label: country.name_es,
              renderLabel: (
                <div className="tw-flex tw-items-center tw-gap-2">
                  <span
                    className={`flag-icon flag-icon-${country.value.toLowerCase()} flag-icon-squared`}
                  >
                  </span>
                  <span>{country.name_es}</span>
                </div>
              )
            }))}
            className="tw-w-full"
            placeholder={t('location.modal.country_placeholder')}
            required
            search
            renderOption={(option) => option?.renderLabel}
            renderSelected={(option) => option?.renderLabel || t('location.modal.country_placeholder')}
          />
        </div>
      </div>

      <div className="tw-p-4 tw-bg-gray-50 tw-rounded-lg">
        <Controller
          name="main"
          control={formMethods.control}
          render={({ field }) => (
            <CheckBoxMC
              id="main"
              name={field.name}
              checked={field.value || false}
              onChange={field.onChange}
              className="tw-text-blue-600 focus:tw-ring-blue-500"
            >
              <label
                htmlFor="main"
                className="tw-text-sm tw-text-gray-900 tw-font-medium tw-cursor-pointer tw-mb-0"
              >
                {t('location.modal.main_address_label')}
              </label>
            </CheckBoxMC>
          )}
        />
        {errorMain && (
          <p className="tw-text-red-500 tw-text-xs tw-mt-2">
            {t('location.main_address_required')}
          </p>
        )}
      </div>
    </div>
  );
};

export default ManageCustomerAddressMC;
